import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Phone, MessageSquare, Mail, Send, Linkedin, History, Plus } from "lucide-react";

const CHANNELS = [
    ["call", "Call", Phone],
    ["whatsapp", "WhatsApp", MessageSquare],
    ["email", "Email", Mail],
    ["sms", "SMS", Send],
    ["linkedin", "LinkedIn", Linkedin],
];

const STATUS_TONE = {
    "Initial contact made": "bg-blue-50 text-ikb border-ikb/40",
    "Call back later": "bg-amber-50 text-amber-800 border-amber-200",
    "Not reachable": "bg-rose-50 text-rose-700 border-rose-200",
    "Switched off": "bg-rose-50 text-rose-700 border-rose-200",
    "Call not received": "bg-amber-50 text-amber-800 border-amber-200",
    "Unable to contact on WhatsApp": "bg-rose-50 text-rose-700 border-rose-200",
    "Candidate not interested": "bg-zinc-100 text-zinc-700 border-zinc-300",
    "Interested — sent JD": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Other": "bg-secondary text-foreground border-border",
};

export default function CandidateFollowups({ candidateId }) {
    const [items, setItems] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ status: "", note: "", channel: "call", next_action_at: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get(`/candidates/${candidateId}/followups`),
                api.get("/meta/followup-statuses"),
            ]);
            setItems(a.data?.items || []);
            setStatuses(b.data?.statuses || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [candidateId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.status) return toast.error("Pick a status");
        setBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/followups`, form);
            setForm({ status: "", note: "", channel: "call", next_action_at: "" });
            toast.success("Follow-up logged");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const remove = async (fid) => {
        if (!window.confirm("Delete this follow-up entry?")) return;
        try {
            await api.delete(`/candidates/${candidateId}/followups/${fid}`);
            setItems((xs) => xs.filter((x) => x.id !== fid));
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-6" data-testid="followups-panel">
            <form onSubmit={submit} className="border border-border bg-white p-4 sm:p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <Plus className="w-4 h-4 text-ikb" />
                    <h3 className="font-display font-bold text-base">Log a follow-up</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mb-1 block">Status</label>
                        <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                            <SelectTrigger className="rounded-none h-10" data-testid="followup-status"><SelectValue placeholder="Select a status…" /></SelectTrigger>
                            <SelectContent>
                                {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mb-1 block">Channel</label>
                        <div className="flex gap-1 flex-wrap">
                            {CHANNELS.map(([k, l, Icon]) => (
                                <button
                                    type="button"
                                    key={k}
                                    onClick={() => setForm((f) => ({ ...f, channel: k }))}
                                    className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest border inline-flex items-center gap-1.5 ${
                                        form.channel === k ? "border-ikb bg-blue-50 text-ikb" : "border-border bg-white hover:bg-secondary"
                                    }`}
                                    data-testid={`followup-channel-${k}`}
                                >
                                    <Icon className="w-3 h-3" /> {l}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mb-1 block">Notes</label>
                    <textarea
                        value={form.note}
                        onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                        rows={3}
                        className="w-full border border-border p-2 text-sm rounded-none"
                        placeholder="What did they say? Next steps?"
                        data-testid="followup-note"
                    />
                </div>
                <div className="flex items-end justify-between gap-3 flex-wrap">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mb-1 block">Next action on</label>
                        <input
                            type="datetime-local"
                            value={form.next_action_at}
                            onChange={(e) => setForm((f) => ({ ...f, next_action_at: e.target.value }))}
                            className="border border-border h-9 px-2 text-sm rounded-none"
                            data-testid="followup-next-action"
                        />
                    </div>
                    <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10" data-testid="followup-submit">
                        {busy ? "Saving…" : "Log follow-up →"}
                    </Button>
                </div>
            </form>

            <div>
                <div className="flex items-center gap-2 mb-3">
                    <History className="w-4 h-4 text-foreground/70" />
                    <h3 className="font-display font-bold text-base">Timeline</h3>
                    <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-0.5">{items.length}</span>
                </div>
                {loading ? (
                    <div className="text-sm text-foreground/70 font-mono">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="border border-dashed border-border p-8 text-center text-sm text-foreground/70">
                        No follow-ups logged yet — log the first one above.
                    </div>
                ) : (
                    <ol className="space-y-3">
                        {items.map((it) => {
                            const tone = STATUS_TONE[it.status] || STATUS_TONE.Other;
                            const Channel = (CHANNELS.find((c) => c[0] === it.channel) || ["call", "Call", Phone])[2];
                            return (
                                <li key={it.id} className="border-l-2 border-ikb pl-4 py-1" data-testid={`followup-row-${it.id}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${tone}`}>{it.status}</span>
                                            <span className="text-[11px] font-mono uppercase tracking-widest text-foreground/80 inline-flex items-center gap-1">
                                                <Channel className="w-3 h-3" /> {it.channel}
                                            </span>
                                            <span className="text-[11px] text-foreground/70 font-mono">
                                                {new Date(it.created_at).toLocaleString()} · {it.created_by_name}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => remove(it.id)}
                                            className="text-foreground/60 hover:text-destructive"
                                            data-testid={`followup-delete-${it.id}`}
                                            title="Delete"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    {it.note && (
                                        <p className="text-sm mt-1.5 whitespace-pre-wrap leading-relaxed">{it.note}</p>
                                    )}
                                    {it.next_action_at && (
                                        <div className="text-[11px] font-mono text-ikb mt-1.5">
                                            Next action → {new Date(it.next_action_at).toLocaleString()}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
}
